import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import useApi from "@/hooks/useApi";

// ── Types ──────────────────────────────────────────────────────────────────

interface TelemetryEvent {
  eventType: "page_view" | "feature_click" | "search" | "setting_change";
  eventKey: string;
  detail?: string;
}

// ── Constants ──────────────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 30_000; // flush every 30s
const MAX_QUEUE = 40;             // never queue more than 40 events locally

// ── Hook ───────────────────────────────────────────────────────────────────

/**
 * Lightweight UI telemetry. Batches events and sends them to /AccountTelemetry/batch.
 * Automatically tracks page views on navigation. Call `track()` for feature interactions.
 *
 * Usage:
 *   const { track } = useTrackEvent();
 *   track("feature_click", "AI_Chatbot/Playground");
 *   track("search", "knowledge-base", `${resultCount} results`);
 *   track("setting_change", "AutoAssign/enabled");
 */
export default function useTrackEvent() {
  const api   = useApi();
  const queue = useRef<TelemetryEvent[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const location = useLocation();

  const flush = useCallback(async () => {
    if (queue.current.length === 0) return;
    const batch = queue.current.splice(0, queue.current.length);
    try {
      await api.admin.post("AccountTelemetry/batch", batch);
    } catch {
      // Telemetry failure is non-fatal — drop silently
    }
  }, [api]);

  // Track page views on route change
  useEffect(() => {
    const path = location.pathname;
    // Skip auth/callback routes
    if (path.startsWith("/auth") || path.startsWith("/login") || path.startsWith("/logout")) return;
    enqueue({ eventType: "page_view", eventKey: path });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush on interval
  useEffect(() => {
    timer.current = setInterval(flush, FLUSH_INTERVAL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
      flush(); // flush on unmount
    };
  }, [flush]);

  // Flush on page unload
  useEffect(() => {
    const handleUnload = () => {
      if (queue.current.length === 0) return;
      const batch = queue.current.splice(0, queue.current.length);
      // Use sendBeacon for guaranteed delivery on unload
      const body = JSON.stringify(batch);
      const url  = `${import.meta.env.VITE_ADMIN_API_URL ?? ""}/AccountTelemetry/batch`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  const enqueue = useCallback((event: TelemetryEvent) => {
    if (queue.current.length >= MAX_QUEUE) return; // drop if full
    queue.current.push(event);
  }, []);

  const track = useCallback(
    (eventType: TelemetryEvent["eventType"], eventKey: string, detail?: string) => {
      enqueue({ eventType, eventKey, detail });
    },
    [enqueue]
  );

  return { track };
}
